import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems, takeFirst } from "~/features/app/utils";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { Media } from "~/features/ui/media";
import { Headline } from "~/features/ui/headline";
import { VideoControls } from "~/features/ui/video/video-controls";
import { ProductHeroLinks } from "~/features/hero/product-hero/product-hero-links";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { ProductHeroBottomRow } from "~/features/hero/product-hero/product-hero-bottom-row";
import { DiscoverMore } from "~/features/hero/discover-more";
const fragment = graphql(`
  fragment ProductHeroMedia on hero_productHeroMedia_BlockType {
    media: baseMedia {
      ...Media
    }
    links: productHeroLinks {
      ...ProductHeroLinks
    }
  }
`);
export type TitleSize = "default" | "large";
type Props = FragmentType<typeof fragment> & {
  title: {
    text?: string;
    size?: TitleSize;
  };
  caption?: string;
};
export function ProductHeroMedia(props: Props) {
  const {
    title,
    caption,
    ...rest
  } = props;
  const {
    media,
    links
  } = useFragment(fragment, rest);
  const themeStyle = getThemeStyle({
    mode: "dark"
  });
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const heroRootRef = React.useRef<HTMLDivElement>(null);
  const topSpacing = isAnnouncementBarVisible ? {
    "marginTop": "calc(var(--announcement-bar-height) + var(--header-height))"
  } : {
    "marginTop": "var(--header-height)"
  };
  return <_StyledDiv ref={heroRootRef} $_css={[{
    "borderTopWidth": "0px !important"
  }, themeStyle.background, themeStyle.text]}>
      <_StyledDiv2>
        <_StyledDiv3 />
        <Media {...takeFirst(media)} imageProps={{
        fit: "cover",
        rounded: false,
        loading: "eager",
        priority: true
      }} inlineVideoProps={{
        fit: "cover",
        videoRef,
        rounded: false,
        priority: true,
        controls: <_StyledVideoControls videoRef={videoRef} $_css2={[{
          "position": "absolute",
          "right": "1.6rem",
          "top": "1.6rem",
          "zIndex": "10"
        }, topSpacing]} />
      }} externalVideoProps={{
        fit: "cover",
        rounded: false
      }} />
        <ProductHeroBottomRow left={<_StyledDiv4>
              {title.text && <_StyledHeadline text={title.text} $_css3={[title.size === "default" && {
          "fontSize": "2.4rem",
          "lineHeight": "1.2",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "fontSize": "4.4rem",
            "lineHeight": "1.3",
            "letterSpacing": "0.044rem",
            "fontWeight": "400"
          }
        }, title.size === "large" && {
          "fontSize": "4.2rem",
          "lineHeight": "1.2",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "fontSize": "9.2rem",
            "lineHeight": "1.1",
            "letterSpacing": "0.092rem",
            "fontWeight": "400"
          }
        }]} />}
              <_StyledP>{caption}</_StyledP>
            </_StyledDiv4>} right={hasItems(links) ? <ProductHeroLinks links={(links || []).filter((link): link is NonNullable<typeof link> => link != null)} /> : undefined} />
        <_StyledDiscoverMore rootRef={heroRootRef} />
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-zcpsgr-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-zcpsgr-1"
})({
  "position": "relative",
  "display": "flex",
  "height": "100svh",
  "alignItems": "flex-end"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-zcpsgr-2"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "inset": "0",
  "zIndex": "10",
  "backgroundImage": "linear-gradient(to bottom, var(--tw-gradient-stops))",
  "--tw-gradient-from": "rgb(0 7 4 / 0.2) var(--tw-gradient-from-position)",
  "--tw-gradient-to": "rgb(0 7 4 / 0) var(--tw-gradient-to-position)",
  "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)"
});
var _StyledVideoControls = _styled4(VideoControls).withConfig({
  componentId: "sc-sf-zcpsgr-3"
})`${p => p.$_css2}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-zcpsgr-4"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "1.6rem"
});
var _StyledHeadline = _styled6(Headline).withConfig({
  componentId: "sc-sf-zcpsgr-5"
})`${p => p.$_css3}`;
var _StyledP = _styled7("p").withConfig({
  componentId: "sc-sf-zcpsgr-6"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiscoverMore = /*#__PURE__*/_styled8(DiscoverMore).withConfig({
  displayName: "product-hero-media___StyledDiscoverMore",
  componentId: "sc-1v2gtx6-0"
})({
  "position": "absolute",
  "bottom": "4rem",
  "right": "8rem",
  "zIndex": "20",
  "display": "none",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "display": "flex"
  }
});